import { NavigationModule, NavigationPage } from './types';
import { IS_DEV_TOOLS_ENABLED } from '../../utils/devTools';

/**
 * Role constants matching the Roles class from components/roles/index.jsx
 * Used for module and page-level access control.
 */
const ROLES = {
  /** Operator (Super Admin) - cross-company access */
  OPERATOR: ['Operator'],
  /** HR and Admin roles - company-wide access */
  HR_AND_ADMIN: ['Hr', 'Admin'],
  /** Manager roles - team-level access */
  MANAGER: ['Manager', 'Hr', 'Chief'],
  /** Clocking roles - all logged-in users except Operator */
  CLOCKING: ['Clocking', 'Hr', 'Chief', 'Manager', 'Admin'],
  /**
   * Settings MODULE gate only — Operator is here solely so the Operator console (Companies,
   * Subscriptions, Module Config, …) remains reachable under the Settings tree. NEVER use this on a
   * leaf page that reads company employee/HR data — use HR_AND_ADMIN for those (CLAUDE.md Role Model).
   *
   * INVARIANT: because `useFilteredNavigation` treats a leaf with NO `roles` array as "inherit the
   * module gate", a role-less leaf under Settings is implicitly Operator-visible — and every such leaf
   * is guarded by `SETTINGS_ROLES = ['Hr','Admin']` in App.tsx, so it dead-ends in a 403. EVERY page in
   * this module (direct or sub-module) must therefore declare an explicit `roles` array mirroring its
   * App.tsx route guard. Do not add a role-less page here.
   */
  SETTINGS_MODULE: ['Hr', 'Admin', 'Operator', 'AssetManagement'],
  /**
   * Every authenticated role EXCEPT Operator. Same membership as CLOCKING and deliberately a
   * SEPARATE constant: this file's gates are allow-lists with no "deny" form, so hiding a leaf
   * from the cross-company Operator means naming everyone else. Using CLOCKING for that would
   * read as "this is a clocking surface", which these leaves are not — and would silently move
   * them if CLOCKING's membership ever changes for clocking reasons.
   *
   * Used for the three employee-scoped leaves an Operator has no data behind (Operator is a
   * platform role with no employee record): Pending Actions, Check-ins, My Profile. All three
   * rendered their own error banner for Operator ("Could not load your pending actions",
   * "Failed to load check-ins", "Failed to load profile data") — found 2026-09-04 by
   * verify-operator-scope-leaveconfig PART C. Owner decision: HIDE them, not an empty state.
   */
  EVERYONE_EXCEPT_OPERATOR: ['Clocking', 'Hr', 'Chief', 'Manager', 'Admin'],
  /** HR-only access */
  HR_ONLY: ['Hr'],
  /** HR and Operator access (e.g. Compliance Config) */
  HR_AND_OPERATOR: ['Hr', 'Operator'],
  /** Asset Management add-on role */
  ASSET_MANAGEMENT: ['AssetManagement'],
  /** Core HR module audience (managers + HR + admins) - used to gate every Core HR page
   *  except Assets Management, so an AssetManagement-only user sees just that one entry. */
  CORE_HR: ['Manager', 'Hr', 'Chief', 'Admin'],
  /** Core HR SETTINGS audience. Narrower than CORE_HR because those screens only write to
   *  HR-only endpoints. Must stay in step with CORE_HR_SETTINGS_ROLES in App.tsx, or a
   *  Manager sees a link the route then bounces. */
  CORE_HR_SETTINGS: ['Hr', 'Chief', 'Admin'],
  /** External Integrations. One constant for the page gate and the App.tsx route, so adding Manager
   *  later is a single edit. (It used to gate a top-level `integrations` module too; that module was
   *  removed 2026-08-28 and the page moved into Settings > General. Identical to HR_ONLY today —
   *  kept separate so a future role change to this surface does not silently move every HR_ONLY
   *  screen with it.) */
  INTEGRATIONS: ['Hr'],
  /**
   * Hourly pay §4.3 — who can decide a shift: the employee's able direct managers (the synthetic
   * 'Manager' `useFilteredNavigation` grants anyone with direct reports) or HR. Chief/Admin without
   * reports decide nothing. Mirrors SHIFT_APPROVER_ROLES in App.tsx.
   */
  SHIFT_APPROVERS: ['Manager', 'Hr'],
};

export const INTEGRATIONS_ROLES = ROLES.INTEGRATIONS;

/**
 * Navigation module configuration for the sidebar.
 * Defines all modules with their pages, icons, colors, and role-based access.
 *
 * All paths correspond to actual routes defined in App.tsx.
 * Coming Soon pages are placed inside their parent modules with comingSoon: true.
 *
 * Mapping validated against: .planning/phases/08.2-navigation-revamp/routes mapping.md
 */
/**
 * ── Tab-level visibility configuration ──────────────────────────────────────
 *
 * Some pages carry their own tab strip. Operators can enable/disable those tabs
 * per company exactly the way they enable/disable pages: a `PageTabGroup` is
 * persisted through the SAME `CompanyPageConfig` storage as pages, with
 * `moduleId` = the group id and `pagePath` = the tab id. No extra table, no
 * extra endpoint — `navigation/pages` already round-trips arbitrary
 * (moduleId, pagePath) pairs.
 *
 * To make a new page's tabs configurable:
 *   1. add a `PageTabGroup` entry below,
 *   2. call `usePageTabConfig(<groupId>)` in the page and filter its tab list.
 * ModuleConfig picks the group up automatically — it renders whatever is here.
 */
export interface PageTab {
  id: string;
  title: string;
  icon: string;
  /** Locked on: rendered read-only in ModuleConfig and never hidden at runtime. */
  alwaysEnabled?: boolean;
}

export interface PageTabGroup {
  /** Stored as `CompanyPageConfig.ModuleId` — must stay stable once shipped. */
  id: string;
  /** Section title in ModuleConfig. */
  title: string;
  /** Route of the page that owns these tabs (shown as the group subtitle). */
  pagePath: string;
  /** Single letter shown in the group avatar. */
  initial: string;
  /** Tailwind gradient classes for the group avatar. */
  color: string;
  tabs: PageTab[];
}

export const PROFILE_TAB_MODULE_ID = 'profile-tabs';
export const PERFORMANCE_SNAPSHOT_TAB_MODULE_ID = 'performance-snapshot-tabs';
export const AI_IMPACT_TAB_MODULE_ID = 'ai-impact-tabs';

/** @deprecated shape alias kept for existing imports — use {@link PageTab}. */
export type ProfileTab = PageTab;

export const pageTabGroups: PageTabGroup[] = [
  {
    id: PROFILE_TAB_MODULE_ID,
    title: 'Profile Page Tabs',
    pagePath: '/profile',
    initial: 'P',
    color: 'from-indigo-500 to-blue-600',
    tabs: [
      { id: 'personal', title: 'Personal', icon: 'User', alwaysEnabled: true },
      { id: 'documents', title: 'Documents', icon: 'FileText' },
      { id: 'skills', title: 'Skills', icon: 'Award' },
      { id: 'payroll', title: 'Payroll', icon: 'DollarSign' },
      { id: 'attendance', title: 'Attendance', icon: 'Clock' },
      // Listed so an Operator can switch it off. Without a row here the tab is unconditionally
      // visible on a user's own profile, even in a company whose Leaves module is disabled.
      { id: 'leaves', title: 'Leaves', icon: 'Palmtree' },
      { id: 'map', title: 'Workplace Map', icon: 'MapPin' },
      { id: 'assets', title: 'Assets', icon: 'Laptop' },
      { id: 'businessRoles', title: 'Business Roles', icon: 'UserCog' },
      { id: 'dependents', title: 'Dependents', icon: 'Users' },
    ],
  },
  {
    id: PERFORMANCE_SNAPSHOT_TAB_MODULE_ID,
    title: 'Performance Snapshot',
    pagePath: '/performance-snapshot',
    initial: 'S',
    color: 'from-purple-500 to-indigo-600',
    /**
     * OWNER DECISION 2026-08-21 — Performance Snapshot is the PARENT and it has exactly TWO
     * children here, each independently switchable per company.
     *
     * These are the page's two SECTIONS, not its inner panels. The five panels of the employee
     * section (narrative / strengths / prompts / comparative / decisions) were listed here
     * before and are deliberately NOT any more: the owner asked for two children under this
     * parent, and exposing seven toggles made the parent/child relationship unreadable.
     *
     * NEITHER is `alwaysEnabled` — the owner asked that each can be turned off. The page renders
     * its own empty state when both are, rather than a blank panel.
     */
    tabs: [
      { id: 'employee-snapshot', title: 'performanceSnapshot.sections.employeeSnapshot', icon: 'FileText' },
      { id: 'snapshots-analysis', title: 'performanceSnapshot.sections.snapshotsAnalysis', icon: 'HeartPulse' },
    ],
  },
  {
    id: AI_IMPACT_TAB_MODULE_ID,
    title: 'AI Impact Analysis',
    pagePath: '/ai-impact-analysis',
    initial: 'A',
    color: 'from-violet-500 to-indigo-600',
    /**
     * The page's two SECTIONS, not its inner panels — same parent/child shape as Performance
     * Snapshot above, and readable for the same reason.
     *
     * NEITHER is `alwaysEnabled`: the two tabs have genuinely different audiences (Company analysis
     * is tenant-wide engagement analytics; Employee analysis is a per-person surface), so a company
     * that wants one without the other must be able to say so. The page renders its own
     * "both switched off" card rather than a blank panel.
     *
     * Switching `employee` off is also what stops the page issuing `PerformanceSnapshot/Roster` at
     * all — that request is made lazily, only when the tab is opened.
     */
    tabs: [
      { id: 'company', title: 'aiImpact.tabs.company', icon: 'Building2' },
      { id: 'employee', title: 'aiImpact.tabs.employee', icon: 'UserSearch' },
    ],
  },
];

/** Profile tabs, kept as a named export for the existing UserProfile call sites. */
export const profileTabs: PageTab[] =
  pageTabGroups.find(g => g.id === PROFILE_TAB_MODULE_ID)!.tabs;

export const navigationModules: NavigationModule[] = [
  {
    id: 'home',
    title: 'Home',
    icon: 'Home',
    color: 'from-blue-500 to-blue-600',
    pages: [
      { title: 'Dashboard', path: '/', icon: 'LayoutDashboard' },
      // Operator has no employee record, so this page can only error for them — hidden by role.
      { title: 'Pending Actions', path: '/pending-actions', icon: 'ClipboardCheck', roles: ROLES.EVERYONE_EXCEPT_OPERATOR }
    ]
  },
  {
    id: 'ai-consultant',
    title: 'AI Consultant',
    icon: 'Sparkles',
    color: 'from-violet-500 to-purple-600',
    roles: ROLES.HR_AND_ADMIN,
    pages: [
      { title: 'AI Implementation Consultant', path: '/ai-consultant', icon: 'Sparkles' },
    ]
  },
  {
    id: 'attendance',
    title: 'Time & Attendance',
    icon: 'Clock',
    color: 'from-blue-500 to-indigo-600',
    roles: ROLES.CLOCKING,
    pages: [
      { title: 'Clock In / Out', path: '/attendance', icon: 'Clock' },
      { title: 'Attendance Overview', path: '/attendance/overview', icon: 'Calendar' },
      { title: 'Team Attendance', path: '/attendance/team-attendance', icon: 'Users', roles: ROLES.MANAGER },
      { title: 'Work Schedules', path: '/attendance/work-schedules', icon: 'CalendarDays', roles: ROLES.MANAGER },
      { title: 'Shift Swaps', path: '/shift-swaps', icon: 'ArrowLeftRight' },
      { title: 'Maps', path: '/attendance/maps', icon: 'Map', roles: ROLES.MANAGER },
      { title: 'Correction Requests', path: '/attendance/correction-requests', icon: 'FileEdit' },
      { title: 'Colleagues Availability', path: '/attendance/colleagues-availability', icon: 'BarChart3' },
      { title: 'Reports', path: '/attendance/reports', icon: 'PieChart', roles: ROLES.MANAGER },
      // CO-583 — Overtime, next to Reports because that is where a reviewer looks for it today.
      // NO `roles`, mirroring the App.tsx route, which is now unguarded: the page has two modes and
      // the one a Clocking employee gets ("my overtime") is theirs to read. The label is the plain
      // 'Overtime' key in BOTH modes — the sidebar cannot know which mode the user will land in, and
      // a label that named the approval queue would be wrong for most of the company.
      { title: 'Overtime', path: '/attendance/overtime-approvals', icon: 'ClipboardCheck' },
      // Hourly pay (docs/hourly-pay-spec.md §6, §10). Both are ALSO dropped by
      // `useFilteredNavigation` unless GetUserInfo reports the company switch on (and, for My shifts,
      // payBasis = Hourly) — a static list cannot know that. Keep in step with the App.tsx guards.
      { title: 'Shift Approvals', path: '/attendance/shift-approvals', icon: 'ListChecks', roles: ROLES.SHIFT_APPROVERS },
      { title: 'My Shifts', path: '/attendance/my-shifts', icon: 'CalendarClock', roles: ROLES.EVERYONE_EXCEPT_OPERATOR },
      // Coming Soon
      { title: 'Attendance Dashboard', path: '/attendance-dashboard', icon: 'BarChart3', comingSoon: true },
      { title: 'Duty Roster Management', path: '/duty-roster-management', icon: 'CalendarDays', comingSoon: true },
    ]
  },
  {
    id: 'leaves',
    title: 'Leaves',
    icon: 'Palmtree',
    color: 'from-green-500 to-emerald-600',
    roles: ROLES.CLOCKING,
    pages: [
      { title: 'My Leaves', path: '/my-leaves', icon: 'CalendarDays' },
      { title: 'Leave Approvals', path: '/leave-approvals', icon: 'ClipboardCheck', roles: ROLES.MANAGER },
      { title: 'Team Calendar', path: '/team-calendar', icon: 'CalendarDays', roles: ROLES.MANAGER },
      // { title: 'Return to Work', path: '/return-to-work', icon: 'ClipboardCheck' },
      // { title: 'Compensatory Off', path: '/comp-off', icon: 'Award' },
      // Self-service leaves surfaces: no `roles` array, so they inherit the module's
      // CLOCKING gate (every logged-in non-Operator) exactly like 'My Leaves'. Their
      // App.tsx routes are unguarded to match. Per-company suppression is available to
      // the Operator through page config (`useFilteredNavigation` filters by pagePath,
      // default-enabled) — the same mechanism every other page here relies on.
      { title: 'Short Leave', path: '/short-leave', icon: 'Timer' },
      { title: 'Leave Encashment', path: '/encashment', icon: 'Banknote' },
      // CO-301 — restored to the sidebar. The /recurring-leave route came back in App.tsx but this
      // entry stayed commented out, so the screen was reachable only by typing the URL. No `roles`
      // key, exactly like 'Short Leave' and 'Leave Encashment' above and matching its UNGUARDED
      // App.tsx route: it is employee self-service on a plain [Authorize] controller, so a
      // MANAGER_ROLES gate here would hide it from the Clocking employees it exists for.
      { title: 'Recurring Leave', path: '/recurring-leave', icon: 'Repeat' },
      // { title: 'Year-End Processing', path: '/year-end-processing', icon: 'CalendarCog', roles: ROLES.HR_AND_ADMIN },
      { title: 'Leave Statements', path: '/leave-statements', icon: 'FileText', roles: ROLES.HR_ONLY },
      // Leave Balances accessible via Leave Statements top-banner button (removed from sidebar).
      { title: 'Absence Analytics', path: '/absence-analytics', icon: 'BarChart3', roles: ROLES.HR_ONLY },
    ]
  },
  {
    id: 'people',
    title: 'Core HR',
    icon: 'Users',
    color: 'from-amber-500 to-orange-600',
    // AssetManagement-tagged users (e.g. a Clocking employee given the asset tag) can open this
    // module so the Assets Management page is reachable from the menu - matching its route guard.
    // Every OTHER Core HR page is explicitly gated to ROLES.CORE_HR, so those users see ONLY
    // Assets Management here (same pattern as the Settings module's asset-only entry).
    roles: [...ROLES.CORE_HR, 'AssetManagement'],
    pages: [
      { title: 'Employee Directory', path: '/core-hr/directory', icon: 'Users', roles: ROLES.CORE_HR },
      { title: 'Org Structure', path: '/core-hr/org-structure', icon: 'Network', roles: ROLES.CORE_HR },
      { title: 'Employee Grouping', path: '/core-hr/grouping', icon: 'Users2', roles: ROLES.HR_AND_ADMIN },
      { title: 'Skills Overview', path: '/core-hr/skills-overview', icon: 'Award', roles: ROLES.CORE_HR },
      // Coming Soon
      { title: 'Employee Document Center', path: '/employee-document-center', icon: 'FileText', comingSoon: true, roles: ROLES.CORE_HR },
      // Mirrors the App.tsx guard (MANAGER_ROLES): managers own the manager approval stage of internal
      // movements. Reads are scoped server-side to the caller's own reports.
      { title: 'Employment Lifecycle', path: '/employment-lifecycle', icon: 'RefreshCw', roles: ROLES.MANAGER },
      { title: 'Departments & Locations', path: '/departments-locations', icon: 'MapPin', roles: ROLES.CORE_HR },
      { title: 'Job Titles & Grades', path: '/job-titles-grades', icon: 'GraduationCap', comingSoon: true, roles: ROLES.CORE_HR },
      { title: 'Company Files & Policies', path: '/company-files-hr-policies', icon: 'FolderOpen', comingSoon: true, roles: ROLES.CORE_HR },
      { title: 'Dependents Directory', path: '/core-hr/dependents-directory', icon: 'Users', roles: ROLES.HR_ONLY },
      // Mirrors ASSETS_PAGE_ROLES in App.tsx: Hr implicitly + anyone holding the AssetManagement
      // tag. Admin is not implicitly an asset manager and only appears here once tagged.
      { title: 'Assets Management', path: '/core-hr/assets-management', icon: 'Laptop', roles: [...ROLES.HR_ONLY, ...ROLES.ASSET_MANAGEMENT] },
      { title: 'Document Template Manager', path: '/document-template-manager', icon: 'FileEdit', roles: ROLES.HR_AND_ADMIN },
      { title: 'Team Role Management', path: '/core-hr/team-role-management', icon: 'UserCog', roles: ROLES.CORE_HR },
      { title: 'HR Process Automation', path: '/hr-process-automation', icon: 'Cog', comingSoon: true, roles: ROLES.CORE_HR },
      { title: 'Core HR Reports', path: '/core-hr/reports', icon: 'PieChart', roles: ROLES.HR_AND_ADMIN },
      { title: 'Bulk Document Upload', path: '/core-hr/bulk-document-upload', icon: 'Upload', roles: ROLES.HR_AND_ADMIN },
    ]
  },
  {
    id: 'recruitment',
    title: 'Recruitment',
    icon: 'UserPlus',
    color: 'from-sky-500 to-blue-600',
    roles: [...ROLES.HR_AND_ADMIN],
    pages: [
      { title: 'Dashboard', path: '/recruitment-dashboard', icon: 'LayoutDashboard' },
      { title: 'Job Openings', path: '/recruitment/job-openings', icon: 'Briefcase' },
      { title: 'Candidates', path: '/recruitment/candidates', icon: 'Users' },
      { title: 'Hiring Pipeline', path: '/recruitment/pipeline', icon: 'Kanban' },
      { title: 'Interviews', path: '/recruitment/interviews', icon: 'CalendarCheck' },
      { title: 'Offers', path: '/recruitment/offers', icon: 'FileCheck' },
      { title: 'Recruitment Settings', path: '/settings/recruitment', icon: 'Settings', roles: ROLES.HR_ONLY },
    ]
  },
  {
    id: 'impact',
    title: 'Employee Impact',
    icon: 'TrendingUp',
    color: 'from-pink-500 to-rose-500',
    pages: [],
    subModules: [
      {
        id: 'impact-engagement',
        title: 'Engagement',
        icon: 'Heart',
        pages: [
          { title: 'Dashboard', path: '/employee-impact/engagement-dashboard', icon: 'LayoutDashboard', roles: ROLES.MANAGER },
          { title: 'Recognition', path: '/employee-impact/recognition-and-feedback', icon: 'Heart' },
          { title: 'eNPS', path: '/employee-impact/enps', icon: 'BarChart3' },
          { title: 'Reflections', path: '/employee-impact/reflections', icon: 'BookOpen' },
          { title: 'Team Hub', path: '/employee-impact/team-hub', icon: 'Users', roles: ROLES.MANAGER },
          { title: 'Team Reflections', path: '/employee-impact/team-reflections', icon: 'UserCheck', roles: ROLES.MANAGER },
          { title: 'HR eNPS Analytics', path: '/hr-enps-analytics', icon: 'BarChart3', roles: ROLES.HR_AND_ADMIN },
          // Same as Pending Actions: employee-scoped, so hidden from Operator rather than left to error.
          { title: 'Check-ins', path: '/check-ins-hub', icon: 'MessageSquare', roles: ROLES.EVERYONE_EXCEPT_OPERATOR },
        ]
      },
      {
        id: 'impact-performance',
        title: 'Performance',
        icon: 'Target',
        pages: [
          { title: 'Dashboard', path: '/employee-impact/performance-dashboard', icon: 'LayoutDashboard' },
          { title: 'Goals', path: '/employee-impact/goals', icon: 'Target' },
          /* CO-436 — managers author snapshots for their own direct reports, so the entry can no
             longer be ROLES.HR_AND_ADMIN. Deliberately NOT opened to the whole company: a plain
             employee reaches their own snapshot from Pending Actions (notification type 90), and a
             menu entry would only ever hand them an empty roster. ROLES.MANAGER already carries the
             synthetic 'Manager' role that `useFilteredNavigation` grants anyone with direct reports;
             'Admin' is added because ROLES.MANAGER omits it. Keep in step with the route guard in
             App.tsx, which is wider on purpose (the subject must be able to open the page). */
          { title: 'Performance Snapshot', path: '/performance-snapshot', icon: 'FileSearch', roles: [...ROLES.MANAGER, 'Admin'] },
          { title: 'Behavior & Values', path: '/behavior-and-values', icon: 'Heart', comingSoon: true },
          /* CO-4xx — this entry carried NO `roles` key while every neighbour did, so under
             `useFilteredNavigation`'s "inherit the module gate" rule it was visible to every
             authenticated user in a paid tenant with the `impact` module on. That was harmless
             only while the page showed five invented people; its picker is now the REAL roster and
             its company tab is real engagement analytics, so it needs the same audience as
             'Performance Snapshot' above — it consumes the same `PerformanceSnapshot/Roster`
             endpoint and the same Insights endpoints as the Engagement Dashboard.
             Keep in step with the route guard in App.tsx. Unlike Performance Snapshot the guard
             here is NOT deliberately wider: that page's guard is wide because the SUBJECT of a
             snapshot must be able to open their own from Pending Actions. This page has no
             self-service subject path — no deep link, no notification, nothing addressed to an
             employee — so a wider guard would only hand a plain employee an empty roster. */
          { title: 'AI Impact Analysis', path: '/ai-impact-analysis', icon: 'Cpu', roles: [...ROLES.MANAGER, 'Admin'] },
        ]
      },
    ]
  },
  {
    id: 'profile',
    title: 'Profile',
    icon: 'User',
    color: 'from-indigo-500 to-blue-600',
    pages: [
      // `/profile/:userId/personal` resolves against the signed-in user's own id; Operator has none.
      { title: 'My Profile', path: '/profile/:userId/personal', icon: 'User', roles: ROLES.EVERYONE_EXCEPT_OPERATOR },
      { title: 'Public Profile', path: '/public-profile-display', icon: 'Globe' },
      { title: 'Profile Configuration', path: '/profile-configuration-center', icon: 'Settings' },
    ]
  },
  {
    id: 'development',
    title: 'Development',
    icon: 'BookOpen',
    color: 'from-violet-500 to-purple-600',
    pages: [
      { title: 'My Development', path: '/employee-development-hub', icon: 'BookOpen', comingSoon: true },
      { title: 'Create Objective', path: '/development-objectives-creator', icon: 'Target', comingSoon: true },
      { title: 'Objective Details', path: '/development-objective-detail', icon: 'Eye', comingSoon: true },
      { title: 'Team Dashboard', path: '/team-development-dashboard', icon: 'LayoutDashboard', comingSoon: true },
      { title: 'Mentorship', path: '/employee-impact/mentorship', icon: 'GraduationCap' },
    ]
  },
  {
    id: 'payroll',
    title: 'Payroll',
    icon: 'DollarSign',
    color: 'from-emerald-500 to-green-600',
    roles: ROLES.CLOCKING,
    pages: [
      { title: 'Dashboard', path: '/payroll/dashboard', icon: 'BarChart3', roles: ROLES.HR_ONLY },
      { title: 'Payroll Table', path: '/payroll/table', icon: 'Table', roles: ROLES.HR_ONLY },
      { title: 'My Claims', path: '/payroll/my-claims', icon: 'Receipt' },
      { title: 'Claims Management', path: '/payroll/claims', icon: 'ClipboardList', roles: ROLES.HR_ONLY },
      { title: 'Payslips', path: '/payroll/payslips', icon: 'FileText', roles: ROLES.HR_ONLY },
      // Coming Soon
      { title: 'Payroll Run', path: '/payroll-run-wizard', icon: 'Play', comingSoon: true },
      { title: 'Payroll Documents', path: '/payroll-documents-center', icon: 'FileText', comingSoon: true },
      // HR-ONLY (owner decision 2026-09-02) — company-wide salary data; server is HrOnly.
      { title: 'Payroll Reports', path: '/payroll-reports', icon: 'PieChart', roles: ROLES.HR_ONLY },
      { title: 'GOSI Contributions', path: '/payroll/pension', icon: 'Landmark', roles: ROLES.HR_ONLY },
      { title: 'Compliance Center', path: '/payroll/compliance-center', icon: 'ShieldCheck', roles: ROLES.HR_ONLY },
      { title: 'WPS Center', path: '/payroll/wps-center', icon: 'FileSpreadsheet', roles: ROLES.HR_ONLY },
      { title: 'EOS', path: '/eos-center', icon: 'UserMinus' },
    ]
  },
  {
    id: 'tournament',
    title: 'Tournament',
    icon: 'Trophy',
    color: 'from-yellow-500 to-amber-600',
    pages: [
      { title: 'Tournaments', path: '/tournament-management/tournaments', icon: 'Trophy' },
      { title: 'Matches', path: '/tournament-management/matches', icon: 'Swords' },
      { title: 'Referees', path: '/tournament-management/referees', icon: 'Users', roles: ROLES.HR_AND_ADMIN },
      { title: 'Referee Assignment', path: '/tournament-management/referee-assignment', icon: 'UserCog', roles: ROLES.HR_AND_ADMIN },
      { title: 'My Assignments', path: '/tournament-management/my-assignments', icon: 'ClipboardList' },
      { title: 'Work Points', path: '/tournament-management/work-points', icon: 'Calculator', roles: ROLES.HR_AND_ADMIN },
      { title: 'Compensation', path: '/tournament-management/compensation', icon: 'DollarSign', roles: ROLES.HR_ONLY },
      { title: 'Referee Workload', path: '/tournament-management/referee-workload', icon: 'BarChart3', roles: ROLES.HR_AND_ADMIN },
      { title: 'Marketplace', path: '/tournament-management/marketplace', icon: 'ShoppingBag', comingSoon: true }
    ]
  },
  {
    id: 'projects',
    title: 'Projects',
    icon: 'FolderKanban',
    color: 'from-teal-500 to-emerald-600',
    pages: [
      { title: 'Projects List', path: '/projects-list', icon: 'Briefcase', comingSoon: true },
      { title: 'Task Board', path: '/task-board', icon: 'ClipboardList', comingSoon: true },
    ]
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: 'Settings',
    color: 'from-gray-500 to-slate-600',
    isAdmin: true,
    roles: ROLES.SETTINGS_MODULE,
    pages: [
      // Development (flat — Operator-only dev tooling, distinct from product config)
      { title: 'Module Config', path: '/settings/module-config', icon: 'LayoutGrid', roles: ROLES.OPERATOR, group: 'Development' },
      { title: 'AI Settings', path: '/settings/ai-settings', icon: 'Bot', roles: ROLES.OPERATOR, group: 'Development' },
      /*
        Seeding CREATES AND WIPES a tenant's data. `DevToolsRoute` refuses the route outside a dev
        build, so the entry is dropped from the menu there too — an Operator on prod should not be
        offered a link that bounces. The route guard is the gate; this is only the honest menu.
      */
      ...(IS_DEV_TOOLS_ENABLED
        ? ([{ title: 'Seeding (Dev)', path: '/settings/development/seeding', icon: 'Database', roles: ROLES.OPERATOR, group: 'Development' }] as NavigationPage[])
        : []),
      { title: 'Blob Migration', path: '/settings/blob-migration', icon: 'DatabaseBackup', roles: ROLES.OPERATOR, group: 'Development' },
    ],
    // Sub-modules mirror the product modules they configure.
    subModules: [
      {
        id: 'settings-general',
        title: 'General',
        icon: 'Building2',
        pages: [
          { title: 'General Details', path: '/settings/general-details', icon: 'Building2', roles: ROLES.HR_AND_ADMIN },
          // CO-355 — READ-ONLY. The AI quota is set by the Operator on /settings/ai-settings; this
          // entry exists so a tenant that hits the cap can see what the cap is and when it resets.
          { title: 'AI Usage & Limits', path: '/settings/ai-usage', icon: 'Gauge', roles: ROLES.HR_AND_ADMIN },
          { title: 'Companies', path: '/settings/companies', icon: 'Building', roles: ROLES.OPERATOR },
          { title: 'Subscriptions', path: '/settings/operator/subscriptions', icon: 'CreditCard', roles: ROLES.OPERATOR },
          // CO-43. The door to the refund release queue. It sits beside Subscriptions because that is
          // where an operator already goes for money questions, and it is a SEPARATE entry rather than
          // a tab inside that console because a refund above the automatic cap is time-bound work
          // someone has to be able to navigate to, link to, and find without knowing it exists.
          { title: 'Refund Queue', path: '/settings/operator/refunds', icon: 'Banknote', roles: ROLES.OPERATOR },
          { title: 'Platform Terms', path: '/settings/operator/platform-terms', icon: 'FileText', roles: ROLES.OPERATOR },
          { title: 'Company Rules', path: '/settings/operator/company-rules', icon: 'ScrollText', roles: ROLES.OPERATOR },
          // Owner decision §14.1c/11. Beside Company Rules because both edit the same CompanyRules row
          // for another tenant, and an operator already comes here to change how a tenant behaves.
          // Title reuses the HR tab label, so it is already translated in en, ar and fr.
          //
          // ⚠️ This row is nav VISIBILITY, never job enablement. Nav config is default-ENABLED and fails
          // OPEN; the three job flags live on CompanyRules and fail CLOSED. Switching this row off only
          // hides a link from an internal operator: the jobs stay off and no mail is sent. If a
          // switch-off row is ever written for it, the page-config key for a page in a Settings
          // SUB-GROUP is `settings-general`, not the top-level module id (a row under the top-level id
          // is silently dropped and reverted by the next Save).
          { title: 'Engagement Jobs', path: '/settings/operator/engagement-jobs', icon: 'Sparkles', roles: ROLES.OPERATOR },
          // 🔴 E12 (owner decision, 2026-08-20) — business config that has LEFT appsettings.json:
          // the platform default AND the per-company override, both edited at runtime. It sits
          // beside Company Rules because that is where an operator already goes to change how a
          // tenant behaves; it is deliberately ONE entry for the whole class rather than a control
          // bolted onto each feature screen.
          { title: 'Platform Configuration', path: '/settings/platform-config', icon: 'SlidersHorizontal', roles: ROLES.OPERATOR },
          { title: 'Mobile App Version', path: '/settings/operator/app-version', icon: 'Smartphone', roles: ROLES.OPERATOR },
          { title: 'Organizations', path: '/settings/organizations', icon: 'Network', roles: ROLES.OPERATOR },
          { title: 'Currency & Banks', path: '/settings/currency-and-banks', icon: 'Banknote', roles: ROLES.HR_AND_ADMIN },
          { title: 'Attachment Types', path: '/settings/general/attachment-types', icon: 'Paperclip', roles: ROLES.HR_AND_ADMIN },
          { title: 'Date Formats', path: '/settings/general/date-formats', icon: 'CalendarDays', roles: ROLES.OPERATOR },
          // CO-343: Operator, not HR_AND_ADMIN — the backing endpoint is Policy="Operator" and returns
          // every tenant's rows (no CompanyId filter), so Hr saw the screen but was refused the data.
          { title: 'System Emails', path: '/settings/system-emails', icon: 'Mail', roles: ROLES.OPERATOR },
          { title: 'Files Configuration', path: '/settings/general/file-upload-settings', icon: 'FileUp', roles: ROLES.OPERATOR },
          { title: 'OCR Configuration', path: '/settings/general/ocr-settings', icon: 'ScanLine', roles: ROLES.HR_AND_ADMIN },
          { title: 'Terms & Policies', path: '/settings/company/terms-and-policies', icon: 'FileText', roles: ROLES.HR_ONLY },
          { title: 'Notification Management', path: '/settings/notification-management', icon: 'BellRing', roles: ROLES.HR_AND_ADMIN },
          /*
            Moved here 2026-08-28 from its own top-level `integrations` MODULE, which held this one
            page. The PATH is unchanged (`/settings/general/external-integrations`) — it always lived
            under a Settings URL — so every route, guard and deep link is untouched; only the sidebar
            placement changed.

            `ROLES.INTEGRATIONS` is kept deliberately rather than collapsed into `ROLES.HR_ONLY`: the
            two are element-for-element identical (`['Hr']`), App.tsx still imports
            `INTEGRATIONS_ROLES` for this exact route, and swapping the constant would move ZERO roles
            while making a role change look like it happened.

            🔴 THE SWITCH-OFF KEY CHANGED SHAPE. This surface used to be swept as a MODULE row
            (`integrations`) in scripts/golive/demo-surface-switchoff.sql. With the module gone that
            key matches nothing, so it is now swept as a PAGE row under `settings-general` — the
            SUB-GROUP id, because that is what the Operator UI writes. A page with no
            `CompanyPageConfig` row reads as ENABLED everywhere (CO-496), so had the SQL not been
            updated in the same change this demo surface would have shipped VISIBLE to all 20 live
            tenants.
          */
          { title: 'External Integrations', path: '/settings/general/external-integrations', icon: 'Plug', roles: ROLES.INTEGRATIONS },
          /*
            🔴 UI-ONLY PREVIEW (no backend). A design preview of the chatbot configuration surface,
            ported from the rocket.new prototype for demo and review; it persists nothing.

            It is registered here so it is SWITCHABLE per company (Module Config → this row writes
            `settings-general:/settings/general/chatbot-analysis`) and so `PageConfigGuard` — which
            resolves by PATH and wraps MasterLayout's outlet — withholds the surface rather than
            merely hiding this entry.

            ⚠️ Registering it does NOT switch it off. A page with no `CompanyPageConfig` row reads as
            ENABLED at every filter and on the server, so a new page ships VISIBLE to every live
            tenant (the External Integrations precedent, CO-496). The thing that actually turns it
            off across the fleet is `scripts/golive/demo-surface-switchoff.sql`, whose `@Pages` list
            names this exact path. Rename the route and you must rename it there too, or the sweep
            misses it silently and reports success.
          */
          { title: 'Chatbot Settings & Analysis', path: '/settings/general/chatbot-analysis', icon: 'Bot', roles: ROLES.HR_AND_ADMIN },
        ]
      },
      {
        /**
         * Customer-facing billing (CO-05). Its own sub-module ON PURPOSE.
         *
         * It must NOT sit in `settings-general` beside the Operator `Subscriptions`
         * entry: two near-identical labels one line apart, pointing at opposite
         * audiences (the tenant paying us vs. the platform team managing tenants),
         * is a misclick waiting to happen on a money surface.
         *
         * `HR_AND_ADMIN` mirrors the `SETTINGS_ROLES` guard on /settings/billing in
         * App.tsx exactly. Operator is absent by design, per the Role Model: billing
         * is company data and Operator is a platform actor with no CompanyId.
         * Employees, Managers and Chiefs see no entry at all, not a disabled one.
         *
         * 🔴 CO-239 — ROLE IS NOT THE ONLY GATE. The role gate below is necessary but not
         * sufficient: this entry is ALSO dropped by `useFilteredNavigation` whenever the
         * environment's billing master switch (`Billing:Enabled`, surfaced as
         * `user.company.billingEnabled`) is not true. That condition cannot live here —
         * this module list is a static description, and the flag arrives at runtime with
         * `GetUserInfo`. With billing off the page renders against a subscription row no
         * billing job has ever touched, so a backfilled perpetual tenant is told it is "N
         * seats over" (CommittedSeats = 0 makes the whole headcount read as overage) and a
         * Free tenant's checkout dead-ends on a raw 409 BILLING_DISABLED.
         * Keep the two gates in step with the App.tsx route guard for the same path.
         */
        id: 'settings-billing',
        title: 'Billing & Subscription',
        icon: 'Wallet',
        roles: ROLES.HR_AND_ADMIN,
        pages: [
          { title: 'Billing', path: '/settings/billing', icon: 'Wallet', roles: ROLES.HR_AND_ADMIN },
        ]
      },
      {
        id: 'settings-attendance',
        title: 'Time & Attendance',
        icon: 'Clock',
        pages: [
          { title: 'Attendance Rules', path: '/settings/attendance/work-schedule-config', icon: 'Shield', roles: ROLES.HR_AND_ADMIN },
          // 'Holidays' removed 2026-09-09: the per-country grid is gone and its replacement lives
          // as a TAB inside 'Leave Settings & Policies' below, which is HR_ONLY. Deleted rather
          // than repointed because HR_AND_ADMIN here would show Admins a link that dead-ends on
          // the HR-only guard.
          { title: 'Workplace Map', path: '/settings/attendance/workplace-map', icon: 'MapPin', roles: ROLES.HR_AND_ADMIN },
          { title: 'Face Recognition', path: '/settings/attendance/face-recognition', icon: 'ScanFace', roles: ROLES.OPERATOR },
          { title: 'Wialon Settings', path: '/settings/attendance/wialon', icon: 'Navigation', roles: ROLES.OPERATOR },
        ]
      },
      {
        id: 'settings-leaves',
        title: 'Leaves',
        icon: 'Palmtree',
        pages: [
          { title: 'Leave Settings & Policies', path: '/leave-policies', icon: 'Settings2', roles: ROLES.HR_ONLY },
          { title: 'Statutory Templates', path: '/statutory-leave-templates', icon: 'Scale', roles: ROLES.HR_ONLY },
          // Operator-only, config-only surface for the 2026-07-29 owner carve-out (leave types +
          // per-country leave rules are company CONFIGURATION, not employee data). Roles here must stay
          // identical to the App.tsx guard on /settings/operator/leave-configuration (OPERATOR_ROLES).
          { title: 'Leave Configuration', path: '/settings/operator/leave-configuration', icon: 'SlidersHorizontal', roles: ROLES.OPERATOR },
        ]
      },
      {
        id: 'settings-payroll',
        title: 'Payroll',
        icon: 'DollarSign',
        pages: [
          { title: 'Gross Pays', path: '/settings/payroll/gross-pays', icon: 'DollarSign', roles: ROLES.HR_AND_ADMIN },
          { title: 'Claims Settings', path: '/settings/payroll/claims', icon: 'Receipt', roles: ROLES.HR_ONLY },
          { title: 'Variable Pays', path: '/settings/payroll/variable-pays', icon: 'TrendingUp', roles: ROLES.HR_ONLY },
          { title: 'Benefits', path: '/settings/payroll/benefits', icon: 'Gift', roles: ROLES.HR_ONLY },
          { title: 'End of Service', path: '/settings/payroll/end-of-service', icon: 'UserMinus', roles: ROLES.HR_ONLY },
          { title: 'Overtime & Attendance Pay', path: '/settings/payroll/attendance', icon: 'Timer', roles: ROLES.HR_ONLY },
          // Hourly pay §3.3. HR_ONLY, mirroring the App.tsx guard and its overtime-pay sibling above.
          { title: 'Hourly Pay', path: '/settings/payroll/hourly-pay', icon: 'Clock4', roles: ROLES.HR_ONLY },
          { title: 'Payslips Config', path: '/settings/payroll/payslips', icon: 'FileText', roles: ROLES.HR_AND_ADMIN },
          { title: 'Compliance Config', path: '/settings/payroll/compliance', icon: 'ShieldCheck', roles: ROLES.HR_AND_OPERATOR },
        ]
      },
      {
        id: 'settings-people',
        title: 'Core HR',
        icon: 'Users',
        pages: [
          { title: 'Welcome Emails', path: '/core-hr/welcome-emails', icon: 'Mail', roles: ROLES.HR_AND_ADMIN },
          { title: 'Lifecycle Settings', path: '/settings/lifecycle', icon: 'Settings2', roles: ROLES.HR_AND_ADMIN },
          { title: 'Asset Management', path: '/settings/core-hr/asset-management', icon: 'Laptop', roles: ['Hr', 'AssetManagement', 'Admin'], adminVisible: true },
          { title: 'Team Role Management', path: '/settings/core-hr/team-role-management', icon: 'UserCog', roles: ROLES.CORE_HR_SETTINGS },
          { title: 'Core HR Settings', path: '/settings/core-hr-settings', icon: 'Settings', comingSoon: true, roles: ROLES.HR_AND_ADMIN },
        ]
      },
      {
        id: 'settings-impact',
        title: 'Employee Impact',
        icon: 'TrendingUp',
        pages: [
          { title: 'Recognition Privacy', path: '/settings/employee-impact/recognition-privacy', icon: 'Lock', roles: ROLES.HR_AND_ADMIN },
          { title: 'Recognition Values', path: '/settings/employee-impact/recognition-values', icon: 'Heart', roles: ROLES.HR_AND_ADMIN },
        ]
      },
      {
        id: 'settings-projects',
        title: 'Projects',
        icon: 'FolderKanban',
        pages: [
          { title: 'Projects', path: '/settings/project-management/projects', icon: 'FolderKanban', roles: ROLES.HR_AND_ADMIN },
        ]
      },
      {
        id: 'settings-profile',
        title: 'Profile',
        icon: 'User',
        pages: [
          { title: 'Roles & Authorization', path: '/profile-authorization-settings', icon: 'Shield', roles: ROLES.HR_AND_ADMIN },
          { title: 'Profile Settings', path: '/profile-settings', icon: 'User', roles: ROLES.HR_AND_ADMIN },
          { title: 'Public Profile Settings', path: '/hr-admin-public-profile-settings', icon: 'Globe', roles: ROLES.HR_AND_ADMIN },
        ]
      },
    ]
  }
];

/**
 * Look up a navigation page's (English) title by its route path.
 *
 * Used to localize assistant navigation deep-links: the returned title is a
 * translation key that goes through `t()`, so a route the assistant hands back
 * ("/attendance/team-attendance") renders with the same label as the sidebar.
 * Returns `undefined` when no page matches (caller falls back to the
 * backend-provided label). Trailing slashes and query/hash are ignored.
 */
export function findNavPageTitleByPath(path: string): string | undefined {
  if (!path) return undefined;
  const normalize = (p: string) => {
    const clean = p.split('?')[0].split('#')[0];
    return clean.length > 1 ? clean.replace(/\/+$/, '') : clean;
  };
  const target = normalize(path);
  for (const module of navigationModules) {
    for (const page of module.pages) {
      if (normalize(page.path) === target) return page.title;
    }
    for (const sub of module.subModules ?? []) {
      for (const page of sub.pages) {
        if (normalize(page.path) === target) return page.title;
      }
    }
  }
  return undefined;
}

/**
 * The page `/` should land on when a company has the Home module switched off.
 *
 * `modules` must already be role- and config-filtered, i.e. straight from `useFilteredNavigation`,
 * so every page reachable here is one the user can actually open. The FIRST such page wins, which
 * makes the landing page the top of the user's own sidebar. That keeps the choice under the control
 * the Operator already has, module display order, instead of adding a second setting that could
 * disagree with what the sidebar shows.
 *
 * Two paths are skipped: a `comingSoon` page routes to a placeholder, and `/` is the route being
 * resolved. Returns undefined when the user can see nothing at all, which the caller answers with
 * the profile page every user can open.
 */
export function pickLandingPath(modules: NavigationModule[]): string | undefined {
  const usable = (page: NavigationPage) => !page.comingSoon && page.path !== '/';
  for (const module of modules) {
    // Direct pages render above sub-modules in the sidebar, so they are considered first here too.
    const direct = module.pages.find(usable);
    if (direct) return direct.path;
    for (const sub of module.subModules ?? []) {
      const nested = sub.pages.find(usable);
      if (nested) return nested.path;
    }
  }
  return undefined;
}

/**
 * Mobile-specific module and page configuration.
 * These IDs are used by the Flutter mobile app.
 * Operators can toggle them per company via the ModuleConfig settings page.
 */

export interface MobileModule {
  id: string;           // moduleId stored in CompanyModuleConfig
  title: string;        // Display label
  icon: string;         // Lucide icon name
  color: string;        // Tailwind gradient
  pages: MobilePage[];  // Sub-pages within this module
}

export interface MobilePage {
  id: string;          // pagePath value stored in CompanyPageConfig
  title: string;       // Display label
  icon: string;        // Lucide icon name
  alwaysEnabled?: boolean;
}

export const mobileModules: MobileModule[] = [
  {
    id: 'mobile-attendance',
    title: 'Attendance',
    icon: 'Clock',
    color: 'from-blue-500 to-indigo-600',
    pages: [
      { id: 'mobile/attendance/clocking', title: 'Clocking', icon: 'Fingerprint' },
      { id: 'mobile/attendance/timesheet', title: 'Timesheet', icon: 'Timer' },
      { id: 'mobile/attendance/schedule', title: 'Schedule', icon: 'CalendarDays' },
      { id: 'mobile/attendance/maps', title: 'Maps', icon: 'MapPin' },
      // CO-583 — the mobile overtime approvals screen. Listed here for ONE reason: this array is
      // what the tenant page switch is built from, so a mobile page missing from it can never be
      // turned off for a company. A shipped screen with no switch is the gap this closes; the
      // label is the existing 'Overtime Approvals' key (en/ar/fr), rendered through t(page.title).
      { id: 'mobile/attendance/overtime-approvals', title: 'Overtime Approvals', icon: 'ClipboardCheck' },
      // Checked by the app (flutter ui/attendance/home.dart) but was never switchable — same gap as above.
      { id: 'mobile/attendance/shift-swap', title: 'Shift Swap', icon: 'ArrowLeftRight' },
      // Hourly pay (docs/hourly-pay-spec.md §10) — the Mac lane's Shifts tab and the approval queue.
      // Registered here so a tenant can switch them off; the app gates them on GetUserInfo too.
      { id: 'mobile/attendance/my-shifts', title: 'My Shifts', icon: 'CalendarClock' },
      { id: 'mobile/attendance/shift-approvals', title: 'Shift Approvals', icon: 'ListChecks' },
    ],
  },
  {
    id: 'mobile-leaves',
    title: 'Time-off',
    icon: 'Palmtree',
    color: 'from-green-500 to-emerald-600',
    pages: [
      { id: 'mobile/leaves/balances', title: 'Balances', icon: 'Scale' },
      { id: 'mobile/leaves/leaves', title: 'Leaves', icon: 'CalendarDays' },
      { id: 'mobile/leaves/holidays', title: 'Holidays', icon: 'Palmtree' },
      { id: 'mobile/leaves/statements', title: 'Statements', icon: 'FileText' },
      { id: 'mobile/leaves/team-calendar', title: 'Team Calendar', icon: 'CalendarRange' },
    ],
  },
  {
    id: 'mobile-employees',
    title: 'Employees',
    icon: 'Users',
    color: 'from-amber-500 to-orange-600',
    pages: [
      { id: 'mobile/employees/list', title: 'Employee List', icon: 'Users', alwaysEnabled: true },
    ],
  },
  {
    id: 'mobile-payroll',
    title: 'Payroll',
    icon: 'Banknote',
    color: 'from-emerald-500 to-green-600',
    pages: [
      { id: 'mobile/payroll/dashboard', title: 'Dashboard', icon: 'LayoutDashboard' },
      { id: 'mobile/payroll/my-claims', title: 'My Claims', icon: 'CalendarDays' },
      { id: 'mobile/payroll/claims', title: 'Claims', icon: 'Timer' },
      // Checked by the app (flutter ui/payroll/homePayroll.dart) but was never switchable.
      { id: 'mobile/payroll/payslips', title: 'Payslips', icon: 'FileText' },
      // Hourly pay §10 — the earnings tile on the Payroll tab (My/Summary + Hourly pay lines).
      { id: 'mobile/payroll/earnings', title: 'Earnings', icon: 'Coins' },
    ],
  },
  {
    id: 'mobile-performance',
    title: 'Employee Impact',
    icon: 'TrendingUp',
    color: 'from-pink-500 to-rose-500',
    pages: [
      { id: 'mobile/performance/dashboard', title: 'Dashboard', icon: 'LayoutDashboard' },
      { id: 'mobile/performance/feedback', title: 'Continuous Feedback', icon: 'MessageSquare' },
    ],
  },
  {
    id: 'mobile-tournament',
    title: 'Tournaments',
    icon: 'Trophy',
    color: 'from-yellow-500 to-amber-600',
    pages: [
      { id: 'mobile/tournaments', title: 'Tournaments', icon: 'Trophy' },
      { id: 'mobile/matches', title: 'Matches', icon: 'Swords' },
      { id: 'mobile/referee-assignment', title: 'Referee Assignment', icon: 'UserCog' },
      { id: 'mobile/referee-management', title: 'Referee Management', icon: 'UserCog' },
      { id: 'mobile/my-assignments', title: 'My Assignments', icon: 'ClipboardList' },
      { id: 'mobile/work-points', title: 'Work Points', icon: 'Calculator' },
    ],
  },
];

// NOTE: `mobile-assignments` and `mobile-goals` are intentionally NOT registered as standalone modules.
// My Assignments is a sub-page of the Tournaments module (toggle via `mobile/my-assignments` page).
// Goals has no dedicated screen yet — add it here when the Goals feature is built.

// Backwards compatibility — keep old exports for existing code that references them
export const MOBILE_MODULE_ID = 'mobile-tournament';
export const mobileTournamentPages = mobileModules.find(m => m.id === 'mobile-tournament')!.pages;

/**
 * The registry entry that owns a concrete URL, used by `PageConfigGuard` to decide whether the
 * company has this page switched off.
 *
 * 🔴 WHY THIS EXISTS: the Operator's page/module toggles were NAVIGATION-ONLY. `useFilteredNavigation`
 * (sidebar) and `usePageTabConfig` (tab strips) were the sole consumers of `getPageConfig()`, and no
 * route guard read it at all — `RoleProtectedRoute` checks role and nothing else. So a "disabled" page
 * still rendered in full for a typed URL, a bookmark, an old email link, or an AI-assistant deep link
 * (`NavigationCatalog` hands back routes without consulting company config). Hiding the sidebar entry
 * was never the same as making the page unreachable.
 *
 * Matching mirrors the sidebar exactly so the two can never disagree:
 *   - an EXACT match wins outright, and is checked across every module before any prefix match;
 *   - otherwise the LONGEST static prefix wins, so `/task-board/42` resolves to `/task-board` and a
 *     detail route inherits its parent page's switch;
 *   - a registry path's parameters are not part of the prefix (`/x/:id` matches on `/x`);
 *   - `/` NEVER prefix-matches, or it would own every URL in the app.
 *
 * Returns the REGISTRY path, not the caller's — that is the value `CompanyPageConfigBase.PagePath`
 * stores, so the result can be compared against config rows directly. `undefined` means the URL is not
 * in the registry at all, which the caller must treat as ALLOW: routes with no sidebar entry (callbacks,
 * redirects, detail views) have no switch and must never be blocked by one that does not exist.
 */
export interface NavEntryRef {
  /** Owning TOP-LEVEL module id — matches `CompanyModuleConfigBase.ModuleId`. */
  moduleId: string;
  /**
   * CO-503 — the SUB-MODULE id when the page was matched under `module.subModules`, else undefined.
   *
   * 🔴 WHY THIS EXISTS: this function used to return the PARENT module id and nothing else for a page
   * living in a sub-module, so a `CompanyModuleConfig` row keyed on the sub-module id (e.g.
   * `impact-performance`, which owns `/ai-impact-analysis`) could never match anything and was inert.
   * `/api/navigation/modules/company/{id}` accepts and persists such rows, and one written as
   * `isEnabled:false` left the page fully reachable — runtime-proven 2026-08-28 by `G5`/`G5-CTL` in
   * `campaign-2026-08-29a/navconfig-RESULTS.txt`.
   *
   * Callers must treat this as an ADDITIONAL id to test, never a replacement: a row on either id
   * disables, and — per CO-496's default-enabled contract — the ABSENCE of a row on either still
   * means ENABLED. Nothing here may ever turn a missing row into a block.
   */
  subModuleId?: string;
  /** Registry page path — matches `CompanyPageConfigBase.PagePath`. */
  pagePath: string;
}

export function findNavEntryByPath(path: string): NavEntryRef | undefined {
  if (!path) return undefined;

  const normalize = (p: string) => {
    const clean = p.split('?')[0].split('#')[0];
    return clean.length > 1 ? clean.replace(/\/+$/, '') : clean;
  };
  // A registry path may carry route params (":userId"). Only the static head is matchable.
  const staticHead = (p: string) => {
    const i = p.indexOf('/:');
    return normalize(i === -1 ? p : p.slice(0, i));
  };

  const target = normalize(path);

  // Pass 1 — exact match anywhere in the registry beats any prefix match. Run to completion first so
  // a shorter exact hit is never lost to a longer prefix hit found earlier.
  for (const module of navigationModules) {
    for (const page of module.pages) {
      if (normalize(page.path) === target) return { moduleId: module.id, pagePath: page.path };
    }
    for (const sub of module.subModules ?? []) {
      for (const page of sub.pages) {
        if (normalize(page.path) === target)
          return { moduleId: module.id, subModuleId: sub.id, pagePath: page.path };
      }
    }
  }

  // Pass 2 — longest static prefix.
  let best: NavEntryRef | undefined;
  let bestLength = -1;
  const consider = (moduleId: string, subModuleId: string | undefined, page: NavigationPage) => {
    const head = staticHead(page.path);
    if (head === '/' || head === '') return; // never let the dashboard own every URL
    if (target !== head && !target.startsWith(`${head}/`)) return;
    if (head.length > bestLength) {
      bestLength = head.length;
      best = { moduleId, subModuleId, pagePath: page.path };
    }
  };
  for (const module of navigationModules) {
    for (const page of module.pages) consider(module.id, undefined, page);
    for (const sub of module.subModules ?? []) {
      for (const page of sub.pages) consider(module.id, sub.id, page);
    }
  }
  return best;
}
